'use client'

import * as React from 'react'
import { cva } from 'class-variance-authority'
import type { VariantProps } from 'class-variance-authority'

import type { ButtonProps as ButtonPrimitiveProps } from '@/components/animate-ui/primitives/buttons/button'
import { Button as ButtonPrimitive } from '@/components/animate-ui/primitives/buttons/button'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[box-shadow,_color,_background-color,_border-color,_outline-color,_text-decoration-color,_fill,_stroke] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        accent: 'bg-accent text-accent-foreground shadow-xs hover:bg-accent/90',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-accent/70',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/60',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-xs': "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-8 rounded-md',
        'icon-lg': 'size-10 rounded-md',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

// The primitive's `ButtonPrimitiveProps` types `asChild`/`children` as a
// strict discriminated union (asChild: true requires children to be a
// single ReactElement) so the underlying Radix-style `Slot` merge is type
// safe. That's too strict for the system-wide `Button` this file exports:
// the ~150 existing call sites (ported straight from the plain shadcn
// button) pass plain `asChild?: boolean` plus arbitrary multi-node
// `children`, and third-party components (react-day-picker's `DayButton`,
// etc.) spread in native DOM handler types like `onAnimationStart` /
// `onDragStart` that collide with Motion's animation-definition-based
// overrides of those same prop names. Re-widen both here to match the
// original shadcn contract; the primitive's runtime `Slot` still guards
// itself (`React.isValidElement` check) for genuine `asChild` usage, so
// behavior is unchanged — only the compile-time surface is loosened back
// to a superset of `React.ComponentProps<'button'>`.
type ButtonProps = Omit<
  ButtonPrimitiveProps,
  | 'asChild'
  | 'children'
  | 'onAnimationStart'
  | 'onAnimationEnd'
  | 'onAnimationIteration'
  | 'onDrag'
  | 'onDragStart'
  | 'onDragEnd'
> &
  Pick<
    React.ComponentProps<'button'>,
    | 'onAnimationStart'
    | 'onAnimationEnd'
    | 'onAnimationIteration'
    | 'onDrag'
    | 'onDragStart'
    | 'onDragEnd'
  > &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    children?: React.ReactNode
  }

function Button({
  className,
  variant = 'default',
  size = 'default',
  ...props
}: ButtonProps) {
  return (
    <ButtonPrimitive
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...(props as ButtonPrimitiveProps)}
    />
  )
}

export { Button, buttonVariants, type ButtonProps }
